import { defineStore } from "pinia";
import { getHttpClient } from "@/api/auth";
import { useToast } from "vue-toastification";

export const useFactureStore = defineStore("factures", {
  state: () => ({
    factures: [],
    totalItems: null,
    totalPages: null,
    currentPage: 1,
    itemsPerPage: 10,
    sortBy: "Par_PiDt_Desc",
    typeFilter: "all",
    loading: false,
    error: null,
    hasMorePages: true,
  }),

  getters: {
    getFactures: state => state.factures ?? [],
    getTotalPages: state => state.totalPages || 1,
    getCurrentPage: state => state.currentPage,
    getIsLoading: state => state.loading,
    getError: state => state.error,
    getHasMorePages: state => state.hasMorePages,
    getTypeFilter: state => state.typeFilter,
  },

  actions: {
    async fetchFactures(page = 1, sortBy = this.sortBy, type = this.typeFilter) {
      this.loading = true;
      this.error = null;
      this.currentPage = page;
      this.sortBy = sortBy || "Par_PiDt_Desc";
      this.typeFilter = type || "all";

      try {
        const endpoint =
          this.typeFilter === "recap" ? "/divalto/factures/mes-recaps" : "/divalto/factures/mes-factures";

        const response = await getHttpClient().get(
          `${endpoint}?page=${page}&itemsPerPage=${this.itemsPerPage}&orderby=${this.sortBy}`
        );
        const data = response.data;
        const rawItems = Array.isArray(data) ? data : data.factures || data.recaps || data["hydra:member"] || [];

        let mapped = rawItems.map(item => ({
          PiDt: item.PIDT,
          HtMt: item.HTMT,
          PINO: item.PINO,
          PREFPINO: item.PREFPINO,
          TTCMT: item.TTCMT,
          PIREF: item.PIREF,
          OP: item.OP,
        }));



        this.factures = mapped;
        this.hasMorePages = rawItems.length === this.itemsPerPage;

        this.totalItems = null;
        this.totalPages = this.hasMorePages ? page : page;
      } catch (error) {
        this.error =
          this.typeFilter === "recap"
            ? "Erreur lors du chargement des récapitulatifs"
            : "Erreur lors du chargement des factures";
        this.factures = [];
        this.hasMorePages = false;
        this.totalItems = null;
        this.totalPages = 1;
      } finally {
        this.loading = false;
      }
    },

    async downloadFacture(pino, prefPino, isRecap = false) {
      const toast = useToast();
      this.error = null;

      try {
        const queryParams = new URLSearchParams();
        if (prefPino) {
          queryParams.append("PREFPINO", prefPino);
        }
        if (isRecap) {
          queryParams.append("recap", "1");
        }
        const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";

        const response = await getHttpClient().get(
          `/divalto/factures/content/${encodeURIComponent(pino)}${queryString}`,
          {
            responseType: "json",
          }
        );

        const base64Content =
          typeof response.data === "string" ? response.data : response.data?.content || response.data?.contenu;

        if (!base64Content || base64Content.trim() === "") {
          this.error = "Ce fichier ne contient pas de contenu.";
          toast.error(this.error);
          return;
        }

        let binaryString;
        try {
          binaryString = atob(base64Content);
        } catch (decodeError) {
          this.error = "Le contenu du fichier est invalide ou corrompu.";
          toast.error(this.error);
          return;
        }

        // Convert to binary
        const bytes = Uint8Array.from(binaryString, char => char.charCodeAt(0));

        // Create Blob and download
        const blob = new Blob([bytes], { type: "application/pdf" });
        const prefix = isRecap ? "Recap" : "Facture";
        const filename = `${prefix}_${pino}.pdf`;

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success(isRecap ? "Récapitulatif téléchargé avec succès !" : "Facture téléchargée avec succès !");
      } catch (error) {
        let errorMessage = isRecap
          ? "Erreur lors du téléchargement du récapitulatif"
          : "Erreur lors du téléchargement de la facture";
        if (error.response) {
          errorMessage = `Erreur serveur : ${error.response.status} - ${error.response.statusText}`;
        } else if (error.request) {
          errorMessage = "Aucune réponse du serveur.";
        } else {
          errorMessage = error.message || errorMessage;
        }
        this.error = errorMessage;
        toast.error(errorMessage);
      }
    },

    changePage(page) {
      if (page >= 1 && (this.hasMorePages || page <= this.currentPage)) {
        this.fetchFactures(page, this.sortBy, this.typeFilter);
      }
    },

    changeSort(sortBy) {
      this.sortBy = sortBy;
      this.currentPage = 1;
      this.hasMorePages = true;
      this.fetchFactures(this.currentPage, this.sortBy, this.typeFilter);
    },

    changeTypeFilter(type) {
      this.typeFilter = type;
      this.currentPage = 1;
      this.hasMorePages = true;
      this.fetchFactures(1, this.sortBy, this.typeFilter);
    },
  },
});
